/**
 * Hebrew counting, which English-shaped string interpolation gets wrong.
 *
 * `${n} אנשים` reads correctly from two upwards and is wrong at one: "1 אנשים"
 * is not something a person would write. Hebrew also inflects for gender, so a
 * single helper needs both forms of the noun and which gender the noun is.
 *
 * Not a full i18n layer, and deliberately not: the product is Hebrew-only and a
 * translation framework would be machinery for a language count of one. This is
 * the narrow thing that was actually wrong.
 */

export interface CountForms {
  /** "איש" — used when n is 1. */
  one: string;
  /** "אנשים" — used for everything else. */
  many: string;
  /** Feminine nouns take "אחת" rather than "אחד". */
  feminine?: boolean;
}

export const NOUNS = {
  person: { one: "איש", many: "אנשים" },
  people: { one: "איש קשר", many: "אנשי קשר" },
  event: { one: "אירוע", many: "אירועים" },
  registration: { one: "הרשמה", many: "הרשמות", feminine: true },
  result: { one: "תוצאה", many: "תוצאות", feminine: true },
  group: { one: "קבוצה", many: "קבוצות", feminine: true },
  tag: { one: "תגית", many: "תגיות", feminine: true },
  record: { one: "כרטיס", many: "כרטיסים" },
  day: { one: "יום", many: "ימים" },
  donor: { one: "תורם", many: "תורמים" },
  soul: { one: "נפש", many: "נפשות", feminine: true },
  present: { one: "נוכח", many: "נוכחים" },
  followUp: { one: "משימה", many: "משימות", feminine: true },
} as const satisfies Record<string, CountForms>;

/**
 * "אירוע אחד" · "2 אירועים" · "אין אירועים".
 *
 * Zero reads better as a word than as a digit — "אין אירועים" rather than
 * "0 אירועים" — so it is handled rather than left to the general case.
 */
export function count(n: number, noun: CountForms, zero?: string): string {
  if (n === 0) return zero ?? `אין ${noun.many}`;
  if (n === 1) return `${noun.one} ${noun.feminine ? "אחת" : "אחד"}`;
  return `${n.toLocaleString("he-IL")} ${noun.many}`;
}

/** A date a reader can place. "30.10" is ambiguous the moment a year passes. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "long",
    // The year is dropped only inside the current year, where it adds nothing.
    ...(sameYear ? {} : { year: "numeric" }),
  });
}
